/**
 * History route — a dedicated, bookmarkable view that takes over the main pane
 * (Figma/Docs/Canva style) instead of a transient side sheet. The splat scopes
 * it: a file path → that document's change history; a directory path (or empty
 * splat = project root) → that folder's checkpoint history.
 */
import type { AutomergeUrl } from "@automerge/automerge-repo";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useInodeAt } from "forest/file-tree/use-tree";
import { X } from "lucide-react";
import { isDirInode } from "playground/fs/paths";

import { PendingPane } from "../../-pending";
import { useWorkspace } from "../../-workspace-context";
import { TitleBar } from "../../../components/TitleBar";
import { DirHistoryView } from "../../../history/DirHistoryView";
import { DocHistoryView } from "../../../history/DocHistoryView";

export const Route = createFileRoute("/_workspace/history/$")({
  head: () => ({ meta: [{ title: "History" }] }),
  component: HistoryPage,
});

function HistoryPage() {
  const { _splat } = Route.useParams();
  const path = _splat ?? "";
  // Keyed so a lookup still in flight never pairs one path with another's inode.
  return <PathHistory key={path} path={path} />;
}

function PathHistory({ path }: { path: string }) {
  const { playground, author } = useWorkspace();
  const navigate = useNavigate();
  const inode = useInodeAt(playground, path || undefined);
  const fileUrl =
    inode && !isDirInode(inode) && inode.uri.startsWith("automerge:")
      ? (inode.uri as AutomergeUrl)
      : null;
  const isDir = path === "" || (inode ? isDirInode(inode) : false);

  const close = () => {
    // Files return to their editor; directories/root return to the pinboard.
    if (fileUrl) {
      navigate({ to: "/files/$", params: { _splat: path }, search: {} });
    } else {
      navigate({ to: "/", search: {} });
    }
  };

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <TitleBar
        actions={
          <button
            type="button"
            onClick={close}
            className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            title="Close history"
            aria-label="Close history"
          >
            <X className="h-4 w-4" />
          </button>
        }
      >
        <span className="truncate text-sm font-medium">
          History · {path === "" ? "Project" : path}
        </span>
      </TitleBar>
      {path && inode === undefined ? (
        <PendingPane />
      ) : fileUrl ? (
        <DocHistoryView url={fileUrl} path={path} author={author} />
      ) : isDir ? (
        <DirHistoryView path={path || "/"} />
      ) : (
        <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">
          No history for {path}
        </div>
      )}
    </div>
  );
}
